// Item 409 — does this event have a skeleton the superuser can see?
// The list says so through `pose_available`, but the pose row lands 2-60 s AFTER the event (background queue) and
// clip-ready only patches the row. So for a fresh video event the modal asks for the pose itself every 10 s
// until it exists (404 = not yet), for at most 10 minutes after the event was created.
import { useQuery } from '@tanstack/react-query'
import { eventsApi } from '@/api/endpoints'
import { POSE_POLL_MS, POSE_POLL_WINDOW_MS, POSE_STALE_MS, queryKeys } from '@/config/queryConfig'
import { useAuthStore } from '@/store/authStore'
import type { Event } from '@/types'

export function usePoseAvailable(event: Event): boolean {
  // `=== true`: a store that is not loaded, or a test double, never counts as a superuser
  const isSuperuser = useAuthStore((s) => s?.user?.is_superuser === true)
  const isRecent = (): boolean => {
    const created = Date.parse(event.created_at)
    return Number.isFinite(created) && Date.now() - created < POSE_POLL_WINDOW_MS
  }
  // `pose_expected`: only a Beta camera can ever get a pose row; undefined (older API, mobile) = no request
  const poll = isSuperuser && event.pose_expected === true && event.pose_available !== true && event.media_details?.media_type === 'video' && isRecent()
  const { data } = useQuery({
    queryKey: queryKeys.eventPose(event.id),
    queryFn: () => eventsApi.getEventPose(event.id),
    enabled: poll,
    // 404 is "not yet": no retry, no error state shown anywhere (the gate only reads `data`)
    retry: false,
    refetchInterval: (query) => (query.state.data || !isRecent() ? false : POSE_POLL_MS),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: POSE_STALE_MS,
  })
  if (!isSuperuser) return false
  if (event.pose_available === true) return true
  // a 200 for a row with no body points (pose_busy / failed early) is "no skeleton": no switch, no marks
  return Boolean(data && data.frames.length > 0)
}
